/*
 * Copyright © 2024 Himitsu Lab Limited. All Rights Reserved.
 */
import { useEffect, useState, useCallback } from 'react';
import { phone } from 'phone';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Auth } from 'aws-amplify';
import { Button } from '../../Components';
import { DatePicker } from '../../Components/base/datepicker/datepicker';
import Field from '../../Components/base/field/field';
import Loading from '../../Components/base/loading/loading';
import { Modal, ModalBody, ModalCloseButton, ModalFooter, ModalHeader } from '../../Components/base/modal/modal';
import { PhoneNumberField } from '../../Components/base/phone-number/PhoneNumberField';
import { toastError } from '../../Components/toast';
import { useSignUpHook } from '../../Hooks/SignUp';
import { useSignUpContentHook } from '../../Hooks/SignUpContent';
import { useGetCognitoKeysQuery, useGetReCaptchaQuery } from '../../Services/envApi';
import { useGetSettingValue } from '../../Services/settingReducer';
import { getIsSignUpTermsAccepted } from '../../Services/signUpReducer';
import { getCurrentUser } from '../../Services/userReducer';
import { useAppSelector } from '../../Store/hooks';
import useToggle from '../../Components/_utils/useToggle';
import InitialPage from '../InitialPage';
import BeemgPrimaryLogo from '../../Assets/NewIcons/beemg-logos-primary.svg';
import { GoogleReCaptcha, GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import google from '../../Assets/Images/google.svg';
import { getToken } from '../../Services/tokenReducer';
import Icon from '../../Components/base/icon/icon';

function SignUpContent({ ipAddressData }: { ipAddressData?: any }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const linkData = useGetSettingValue('WEB_LINK');
  const termsAndConditionStatus = useAppSelector(getIsSignUpTermsAccepted);
  const { toggle, isOpen } = useToggle();
  const { data: cognitoKeys, isLoading: cognitoKeysLoading } = useGetCognitoKeysQuery();
  const { data: siteKey, isLoading: siteKeyLoading, error: siteKeyError } = useGetReCaptchaQuery();

  const {
    recaptchaValue,
    serverError,
    onChange,
    signUp,
    maxDob,
    minDob,
    handleSubmit,
    register,
    watch,
    errors,
    control,
    getValues,
    setServerError,
  } = useSignUpContentHook(ipAddressData);
  const [showPassword, setShowPassword] = useState(false);
  const [reCaptchaKey, setReCaptchaKey] = useState(0); // Control reCAPTCHA refresh
  const [isReCaptchaVerified, setIsReCaptchaVerified] = useState(false); // Track verification

  const toggleShowPassword = () => {
    setShowPassword((prev) => !prev);
  };

  const handleVerify = useCallback(
    (token: string) => {
      console.log('reCAPTCHA token received:', { tokenLength: token.length });
      onChange(token);
      setIsReCaptchaVerified(true); // Mark as verified
    },
    [onChange],
  );

  useEffect(() => {
    if (serverError) {
      toastError(t(serverError));
      setServerError('');
      setIsReCaptchaVerified(false); // Reset verification on error
      setReCaptchaKey((prev) => prev + 1); // Refresh reCAPTCHA
    }
  }, [serverError, t, setServerError]);

  useEffect(() => {
    console.log('SignUpContent mounted');
    return () => console.log('SignUpContent unmounted');
  }, []);

  const [passwordError, setPasswordError] = useState('');

  useEffect(() => {
    if (errors.password) {
      const messages = (errors.password.message?.split(',') || []).map((msg) => t(msg));
      setPasswordError(`<ul>${messages.map((msg) => `<li>${msg}</li>`).join('')}</ul>`);
    } else {
      setPasswordError('');
    }
  }, [errors.password, t]);

  const handleCheckPhoneNumber = (phoneNumber: string): boolean => !phone('+' + watch('phoneNumber')).isValid;

  // Reset reCAPTCHA on form submission success
  const handleFormSubmit = async (data: any) => {
    await handleSubmit(signUp)(data);
    if (!serverError) {
      setIsReCaptchaVerified(false); // Reset for next submission
      setReCaptchaKey((prev) => prev + 1); // Refresh reCAPTCHA
    }
  };

  if (cognitoKeysLoading || siteKeyLoading) {
    return <Loading />;
  }

  if (siteKeyError || !siteKey) {
    console.error('Failed to load reCAPTCHA site key:', siteKeyError);
    toastError(t('recaptchaError'));
    return <div>{t('recaptchaError')}</div>;
  }

  return (
    <GoogleReCaptchaProvider reCaptchaKey={siteKey}>
      <div className="flex flex-col items-center">
        <form onSubmit={handleFormSubmit} autoComplete="off" className="mt-2 max-w-[30rem]">
          <div className="grid grid-cols-4 gap-2">
            <div className="md:col-span-2 col-span-4">
              <Field
                {...register('firstName')}
                error={errors.firstName?.message}
                name="firstName"
                data-testid="input_firstName"
                type="text"
                placeholder={`${t('firstName')} *`}
                maxLength={25}
                aria-label={t('firstName')}
                className="capitalize"
              />
            </div>
            <div className="md:col-span-2 col-span-4">
              <Field
                {...register('lastName')}
                error={errors.lastName?.message}
                name="lastName"
                data-testid="input_lastName"
                type="text"
                placeholder={`${t('lastName')} *`}
                maxLength={25}
                aria-label={t('lastName')}
                className="capitalize"
              />
            </div>
            <div className="md:col-span-2 col-span-4">
              <Controller
                control={control}
                name="phoneNumber"
                render={({ field: { onChange, value } }) => (
                  <PhoneNumberField
                    value={value}
                    data-testid="input_phoneNo"
                    placeholder={`${t('phoneNo')} *`}
                    onChange={onChange}
                    aria-label={t('phoneNo')}
                  />
                )}
              />
              {watch('phoneNumber') && handleCheckPhoneNumber(watch('phoneNumber')) && (
                <span
                  id="error_phoneNo"
                  data-testid="error_phoneNo"
                  className="text-red-500 text-sm col-span-4"
                >
                  {t('enterValidCountryCodeAndPhoneNo')}
                </span>
              )}
            </div>
            <div className="col-span-4 w-full">
              <Field
                {...register('email')}
                error={errors.email?.message}
                name="email"
                type="email"
                placeholder={`${t('enterValidEmailID')} *`}
                data-testid="input_email"
                aria-label={t('enterValidEmailID')}
              />
            </div>
            <div className="col-span-4 w-full">
              <Field
                {...register('password')}
                name="password"
                type={showPassword ? 'text' : 'password'}
                data-testid="input_password"
                placeholder={t('password') + ' *'}
                icon={
                  watch('password')?.length > 0 ? (
                    showPassword ? (
                      <Icon icon="VIEW" className="cursor-pointer" onClick={toggleShowPassword} />
                    ) : (
                      <Icon icon="VIEW_SLASH" className="cursor-pointer" onClick={toggleShowPassword} />
                    )
                  ) : undefined
                }
              />
              {passwordError && (
                <span
                  id="error_password"
                  data-testid="error_password"
                  className="text-red-500 text-sm"
                  dangerouslySetInnerHTML={{ __html: passwordError }}
                />
              )}
            </div>
            <div className="md:col-span-4 col-span-2 font-semibold">
              {maxDob && minDob && (
                <DatePicker
                  label={t('dateOfBirth')}
                  name="dateOfBirth"
                  control={control}
                  error={errors.dateOfBirth?.message}
                  minDate={minDob}
                  maxDate={maxDob}
                  placeholderText="yyyy-mm-dd"
                  aria-label={t('dateOfBirth')}
                />
              )}
            </div>
            <div className="md:col-span-4 col-span-2 font-semibold">
              <Field
                {...register('gender')}
                label={t('gender')}
                name="gender"
                data-testid="input_gender"
                type="select"
                error={errors.gender?.message}
                aria-label={t('gender')}
              >
                <option value="Male">{t('male')}</option>
                <option value="Female">{t('female')}</option>
                <option value="Others">{t('others')}</option>
              </Field>
            </div>
            <div className="text-blue-900 ml-1 col-span-4 text-sm font-semibold block md:hidden">
              {t('TheMinimumEntryAgeIs18')}
            </div>
            {!isReCaptchaVerified && (
              <div className="col-span-4 flex justify-center">
                <GoogleReCaptcha
                  key={reCaptchaKey}
                  onVerify={handleVerify}
                  refreshReCaptcha={reCaptchaKey}
                />
              </div>
            )}
          </div>
          <div className="flex flex-col justify-center items-center">
            <Button
              className="w-72 rounded-lg mt-4 mb-2"
              color="save"
              id="btn_next1"
              data-testid="btn_next1"
              disabled={!recaptchaValue || handleCheckPhoneNumber(watch('phoneNumber'))}
              submit
              aria-label={t('next')}
            >
              {t('next')}
            </Button>
            <div>
              <a
                href={`https://${cognitoKeys?.domain}/oauth2/authorize?response_type=token&client_id=${cognitoKeys?.userPoolClientId}&identity_provider=Google&redirect_uri=${linkData}loginSuccessful/`}
              >
                <Button
                  color="google"
                  id="btn_signInWithGoogle"
                  data-testid="btn_signInWithGoogle"
                >
                  <div className="flex items-center">
                    {t('signUpWith')}{' '}
                    <img className="ml-2" width={12} height={12} src={google} alt="" />
                  </div>
                </Button>
              </a>
            </div>
            <span className="flex justify-center hover:underline cursor-pointer mt-2" id="btn_alreadyMember">
              <Link to="/signIn" aria-label={t('alreadyAMember')}>
                {t('alreadyAMember')}?
              </Link>
            </span>
          </div>
        </form>
        <Modal isOpen={isOpen} toggle={toggle} closeOnClickOutside={false}>
          <ModalCloseButton toggle={toggle} />
          <ModalHeader>{t('confirmPhoneNumber')}</ModalHeader>
          <ModalBody>
            {getValues().phoneNumber && (
              <span className="flex">
                <span data-testid="txt_confirm">{t('confirmThatYourPhoneNumberIs')}</span>
                <span className="ml-1 font-semibold">+ {getValues().phoneNumber}</span>
              </span>
            )}
          </ModalBody>
          <ModalFooter>
            <Button id="btn_close" data-testid="btn_close" onClick={toggle} color="footerButton">
              {t('close')}
            </Button>
            <Button id="btn_confirm" data-testid="btn_confirm" color="footerButton">
              {t('confirm')}
            </Button>
          </ModalFooter>
        </Modal>
      </div>
    </GoogleReCaptchaProvider>
  );
}

function SignUp() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const currentUser = useAppSelector(getCurrentUser);
  const userAccessToken = useAppSelector(getToken);
  const { loading, ipAddressData } = useSignUpHook();

  useEffect(() => {
    if (currentUser && userAccessToken && pathname !== '/intro/choosePreference') {
      navigate('/intro/choosePreference');
    }
  }, [currentUser, userAccessToken, navigate, pathname]);

  if (loading) {
    return <Loading />;
  }

  return (
    <InitialPage>
      <div className="flex pl-0 justify-center lg:w-1/3">
        <div className="flex my-auto flex-col p-3 rounded-lg md:opacity-90 bg-none">
          <img src={BeemgPrimaryLogo} alt="Beemg Logo" className="h-32 w-32 mx-auto" />
          <span className="text-4xl sm:text-normal uppercase font-bold mb-4 text-center">
            {t('signUp')}
          </span>
          <SignUpContent ipAddressData={ipAddressData} />
        </div>
      </div>
    </InitialPage>
  );
}

export default SignUp;